import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, BoxProps, Divider, Flex, Text } from '@modusign/design-system';
import { forwardRef } from 'react';

interface BatchActionToolbarProps {
  checkbox?: React.ReactNode;
  children: React.ReactNode;
  selectedItemsCount: number;
}

/**
 * @example
 * <BatchActionToolbar {...props}>
 *   <BatchActionToolbar.Button rightIcon={faChevronDown} onClick={() => {}}>
 *     요청 문서 관리
 *   </BatchActionToolbar.Button>
 *   <BatchActionToolbar.Divider />
 *   <BatchActionToolbar.Button leftIcon={faRotateLeft} onClick={() => {}}>
 *     선택 취소
 *   </BatchActionToolbar.Button>
 * </BatchActionToolbar>
 */
function BatchActionToolbar({ checkbox, children, selectedItemsCount }: BatchActionToolbarProps) {
  if (!selectedItemsCount) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      bg="boxBackgroundEmphasisBase"
      borderRadius="4px"
      height="48px"
      pl={4}
      pr={6}
      width="100%"
    >
      <Box flex="none">{checkbox}</Box>
      <Text
        color="textEmphasisBase"
        fontWeight="medium"
        ml={checkbox ? 5 : 0}
        sx={{ flex: 'none' }}
      >
        선택
      </Text>
      <Flex
        alignItems="center"
        bg="statusBackgroundEmphasisDark"
        borderRadius="12px"
        color="textNeutralLightest"
        fontSize="12px"
        fontWeight="medium"
        height="20px"
        justifyContent="center"
        ml={1}
        mr="16px"
        textAlign="center"
        width="30px"
      >
        {selectedItemsCount}
      </Flex>
      {children}
    </Flex>
  );
}

interface BatchActionToolbarButtonProps extends BoxProps<'button'> {
  leftIcon?: IconDefinition;
  rightIcon?: IconDefinition;
}

const BatchActionToolbarButton = forwardRef<HTMLButtonElement, BatchActionToolbarButtonProps>(
  function BulkActionToolbarButton(
    { children, disabled, leftIcon, rightIcon, position, onClick, ...props },
    ref
  ) {
    return (
      <Flex
        alignItems="center"
        as="button"
        color="textNeutralLight"
        disabled={disabled}
        fontSize="14px"
        position={position}
        ref={ref}
        sx={{
          '&:not(:disabled):hover': {
            color: 'textNeutralDark',
            textDecoration: 'underline',
            textUnderlineOffset: '0.2em',
            '& > svg': {
              color: 'textNeutralDark',
            },
          },
        }}
        type="button"
        onClick={onClick}
        {...props}
      >
        {leftIcon && (
          <Flex
            alignItems="center"
            as={FontAwesomeIcon}
            height="12px"
            icon={leftIcon}
            justifyContent="center"
            mr="2px"
            width="12px"
          />
        )}
        {children}
        {rightIcon && (
          <Flex
            alignItems="center"
            as={FontAwesomeIcon}
            height="12px"
            icon={rightIcon}
            justifyContent="center"
            ml="2px"
            width="12px"
          />
        )}
      </Flex>
    );
  }
);

function BatchActionToolbarDivider() {
  return (
    <Box height="18px" mx={5}>
      <Divider direction="column" />
    </Box>
  );
}

BatchActionToolbar.Button = BatchActionToolbarButton;
BatchActionToolbar.Divider = BatchActionToolbarDivider;
export default BatchActionToolbar;
